import { useEffect, useState, useCallback, useRef } from 'react';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import FcBlock from '~/components/FundRequests/blocks/FcBlock';
import CscBlock from '~/components/FundRequests/blocks/CscBlock';
import FundNameBlock from '~/components/FundRequests/blocks/FundNameBlock';
import RequestAmountBlock from '~/components/FundRequests/blocks/RequestAmountBlock';
import TitlePanel from '~/components/FundRequests/FundRequestDetails/TitlePanel';
import Panel from '~/components/Panel';
import { FaPlus } from 'react-icons/fa6';
import { RxCross2 } from 'react-icons/rx';
import Tabs from '~/components/Tabs';
import Payee from './Payee';
import AddressVerificationModal from '~/components/FundRequests/modals/AddressVerificationModal';
import PartialDisbursementModal from '~/components/FundRequests/modals/PartialDisbursementModal';
import CreatePartialDisbursementModal from '~/components/FundRequests/modals/CreatePartialDisbursementModal';

import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    addFundRequestItemFlag,
    addToQueue,
    assignFinancialCoordinator,
    deleteFundRequestItem,
    fetchAnnualRates,
    fetchFinancialCoordinators,
    refreshFundRequestAvailableBalance,
    fetchFundRequestCategories,
    fetchFundRequestDetail,
    fetchFundRequestGLCodes,
    fetchFundRequestAvailableBalance,
    fetchFundRequestHistory,
    resolveFundRequestItemFlag,
    clearFundRequestItemFlag,
    setFundRequestStatus,
    setStatusApReview,
    setStatusNeedsAdditionalInfo,
    sendNeedsInfoReminder,
    suggestPartialDisbursement,
    createPartialDisbursement,
    revertPartialDisbursement,
} from '~/services/fund-requests';
import { FaExclamationTriangle } from 'react-icons/fa';
import { FaCheck } from 'react-icons/fa6';
import {
    DefaultExpenseItem,
    ExpenseItem,
    FinancialCoordinatorOption,
    FundRequest,
    FundRequestNote,
    HistoryItem,
    TransactionFood,
    TransactionFoodSubmit,
    TransactionTravel,
    TransactionTravelSubmit,
    TransactionUpload,
    Vendor,
} from '~/types/fundrequests';

import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { formatDateIfValid, toUSD } from '~/utils';
import ExpenseInfoDrawer from '~/components/FundRequests/drawers/ExpenseInfoDrawer';
import DocumentPreview from '~/components/FundRequests/drawers/ExpenseInfoDrawer/DocumentPreview';
import {
    FundRequestExpenseTypes,
    FundRequestStatus,
} from '~/enums/fundRequest';
// Inline note composer (modal removed)
import { safeParseHtml } from '~/utils/safeParseHtml';
import { ExpenseItemRow } from './ExpenseItemRow';
import { SelectOption } from '~/types/globals';
import toast from 'react-hot-toast';
import { useFundRequestContext } from '~/context/FundRequestProvider';
import { apiUrl, authHeaders } from '~/services/auth';
import {
    fetchTravelSchema,
    buildTravelDefault,
    TravelSchema,
} from '@shared/expenses/travel';
import { useAuth } from '~/context/AuthProvider';
import HistoricalExpenses from './HistoricalExpenses';
import { useQuery } from '@tanstack/react-query';
import {
    markFundRequestNoteRead,
    addFundRequestNote,
    toggleFundRequestNoteInternal,
} from '~/services/fund-requests';
import { MdCheck } from 'react-icons/md';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import LogoIcon from '../../images/logo/logo-icon.svg';
import FilesTab from '~/components/FundRequests/tabs/FilesTab';
import LogEntryModal from '~/components/FundRequests/modals/LogEntryModal';
import {
    saveFundRequest,
    PendingUpload,
    collectFlaggedComments,
    combineAllDefaultTransactions,
    determineDefaultPaymentType,
} from '~/services/fund-requests-helpers';

type DefaultValues = {
    vendor: Vendor | null;
    transactions_medical: DefaultExpenseItem[];
    transactions_medications: DefaultExpenseItem[];
    transactions_insurance: DefaultExpenseItem[];
    transactions_relocation_lodging: DefaultExpenseItem[];
    transactions_other: DefaultExpenseItem[];
    transactions_travel: (TransactionTravel | TransactionTravelSubmit)[];
    transactions_food: (TransactionFood | TransactionFoodSubmit)[];
    transactions_caregiving: DefaultExpenseItem[];
    transactions_physical_therapy: DefaultExpenseItem[];
    transactions_fundraising: DefaultExpenseItem[];
    description: string;
    ap_invoice_number: string;
    payment_type: string;
    needs_ach_info: boolean;
};

const defaultValues: DefaultValues = {
    vendor: null,
    transactions_medical: [],
    transactions_medications: [],
    transactions_insurance: [],
    transactions_relocation_lodging: [],
    transactions_other: [],
    transactions_travel: [],
    transactions_food: [],
    transactions_caregiving: [],
    transactions_physical_therapy: [],
    transactions_fundraising: [],
    description: '',
    ap_invoice_number: '',
    payment_type: 'CHECK',
    needs_ach_info: false,
};

const extractUploads = (fundRequest: FundRequest | null) => {
    const keys = [
        'transactions_food',
        'transactions_medical',
        'transactions_insurance',
        'transactions_medications',
        'transactions_other',
        'transactions_relocation_lodging',
        'transactions_travel',
        'transactions_caregiving',
        'transactions_physical_therapy',
        'transactions_fundraising',
    ] as const;

    const results: {
        fileName: string;
        imageUrl: string;
        formKey: string;
        expenseCategory: FundRequestExpenseTypes;
        uploadIndex: number;
    }[] = [];

    keys.forEach((key) => {
        const transactions = fundRequest?.[key];
        transactions?.forEach(
            (transaction: ExpenseItem, transactionIndex: number) => {
                // Only process transactions that have uploads (TransactionDefault, TransactionTravel, TransactionFood)
                if ('uploads' in transaction && transaction.uploads) {
                    transaction.uploads.forEach(
                        (
                            uploadItem: TransactionUpload,
                            uploadIndex: number,
                        ) => {
                            // More robust checking: ensure we have an image URL (required) and attempt to get a filename
                            const imageUrl =
                                uploadItem.image_url ||
                                (uploadItem as any).imageUrl;
                            const fileName =
                                uploadItem.filename ||
                                (uploadItem as any).fileName ||
                                `file_${uploadIndex + 1}`;

                            // Only include if we have a valid image URL
                            if (imageUrl) {
                                // Map transaction keys to expense categories
                                const categoryMap: Record<
                                    string,
                                    FundRequestExpenseTypes
                                > = {
                                    transactions_food:
                                        FundRequestExpenseTypes.FOOD,
                                    transactions_medical:
                                        FundRequestExpenseTypes.DEFAULT,
                                    transactions_insurance:
                                        FundRequestExpenseTypes.DEFAULT,
                                    transactions_medications:
                                        FundRequestExpenseTypes.DEFAULT,
                                    transactions_other:
                                        FundRequestExpenseTypes.DEFAULT,
                                    transactions_relocation_lodging:
                                        FundRequestExpenseTypes.DEFAULT,
                                    transactions_travel:
                                        FundRequestExpenseTypes.TRAVEL,
                                    transactions_caregiving:
                                        FundRequestExpenseTypes.DEFAULT,
                                    transactions_physical_therapy:
                                        FundRequestExpenseTypes.DEFAULT,
                                    transactions_fundraising:
                                        FundRequestExpenseTypes.DEFAULT,
                                };

                                results.push({
                                    fileName,
                                    imageUrl,
                                    formKey: `${key}[${transactionIndex}]`,
                                    expenseCategory: categoryMap[key],
                                    uploadIndex,
                                });
                            }
                        },
                    );
                }
            },
        );
    });

    // Add ACH document if it exists
    if (fundRequest?.ach_request?.ach_document_url) {
        results.push({
            fileName:
                fundRequest.ach_request.ach_document_filename || 'ACH Document',
            imageUrl: fundRequest.ach_request.ach_document_url,
            formKey: 'ach_document',
            expenseCategory: FundRequestExpenseTypes.DEFAULT, // or create a new ACH type if needed
            uploadIndex: 0,
        });
    }

    return results;
};

const FundRequestDetail: React.FC = () => {
    const { fundRequest, fundRequestDispatch, formKey, pendingUploads } =
        useFundRequestContext();
    const [openTab, setOpenTab] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(false);
    const [flaggedComments, setFlaggedComments] = useState<string[]>([]);
    const [notes, setNotes] = useState<FundRequestNote[]>([]);
    // Inline notes composer state
    const [newNote, setNewNote] = useState<string>('');
    const [newNoteIsInternal, setNewNoteIsInternal] = useState<boolean>(false);
    const [sendingNote, setSendingNote] = useState<boolean>(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedExpenseCategory, setSelectedExpenseCategory] =
        useState<FundRequestExpenseTypes>();
    const [categories, setCategories] = useState<SelectOption[]>([]);
    const [financialCoordinators, setFinancialCoordinators] = useState<
        FinancialCoordinatorOption[]
    >([]);
    const [glCodes, setGlCodes] = useState<SelectOption[]>([]);
    const [deleteExpenseItem, setDeleteExpenseItem] = useState<{
        formKey: string;
        expenseCategory: FundRequestExpenseTypes;
    }>();
    const [revertPartialChild, setRevertPartialChild] =
        useState<FundRequest | null>(null);
    const [revertingPartialChild, setRevertingPartialChild] =
        useState<boolean>(false);
    // For save operations outside useForm, such as saving flags, use setSaving
    const [saving, setSaving] = useState<boolean>(false);
    const [availableFunds, setAvailableFunds] = useState<number>(0);
    const [rawAvailableFunds, setRawAvailableFunds] = useState<number>(0);
    const [approvedPendingTotal, setApprovedPendingTotal] = useState<number>(0);
    const [unapprovedTotal, setUnapprovedTotal] = useState<number>(0);

    // Unapproved total is provided by the API serializer on detail fetch
    const [hasInsufficientFunds, setHasInsufficientFundsState] =
        useState<boolean>(false);
    const [fundsLoaded, setFundsLoaded] = useState<boolean>(false);

    const setHasInsufficientFunds = (value: boolean) => {
        setHasInsufficientFundsState(value);
    };
    const [isRefreshingFunds, setIsRefreshingFunds] = useState<boolean>(false);
    const [initialFundsChecked, setInitialFundsChecked] =
        useState<boolean>(false);
    const [requestHistory, setRequestHistory] = useState<HistoryItem[]>([]);
    // Notes list scroll container
    const notesListRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (openTab === 3 && notesListRef.current) {
            requestAnimationFrame(() => {
                if (notesListRef.current) {
                    notesListRef.current.scrollTop =
                        notesListRef.current.scrollHeight;
                }
            });
        }
    }, [openTab, notes.length]);
    const [addressVerificationModalOpen, setAddressVerificationModalOpen] =
        useState(false);
    const [partialDisbursementModalOpen, setPartialDisbursementModalOpen] =
        useState(false);
    const [
        createPartialDisbursementModalOpen,
        setCreatePartialDisbursementModalOpen,
    ] = useState(false);
    const [pendingStatusUpdate, setPendingStatusUpdate] = useState<{
        status: FundRequestStatus;
        note: string | null;
    } | null>(null);

    // LogEntry modal state
    const [logEntryModal, setLogEntryModal] = useState<{
        isOpen: boolean;
        data: Record<string, any> | null;
    }>({ isOpen: false, data: null });

    // Document preview state
    const [previewSlideIndex, setPreviewSlideIndex] = useState<number | null>(
        null,
    );
    const [previewUploads, setPreviewUploads] = useState<any[]>([]);
    const previewOpen = previewSlideIndex !== null;

    // Track the formKey and category when a new item is created
    const [newItemFormKey, setNewItemFormKey] = useState<string | null>(null);

    const tabNames = [
        { name: 'History', count: requestHistory.length },
        { name: 'Files', count: extractUploads(fundRequest).length },
        { name: 'Notes', count: notes.length },
    ];
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const methods = useForm<DefaultValues>({ defaultValues });
    const { reset, control, watch } = methods;
    const storedUser = localStorage.getItem('user');
    const parsedUser = storedUser ? JSON.parse(storedUser) : null;
    const { refreshUser, person, user } = useAuth();
    // Load centralized travel schema once
    const [travelSchema, setTravelSchema] = useState<TravelSchema | null>(null);
    useEffect(() => {
        (async () => {
            try {
                const schema = await fetchTravelSchema(apiUrl, authHeaders());
                setTravelSchema(schema);
            } catch (e) {
                console.error('Failed to load travel schema', e);
            }
        })();
    }, []);
    // Track the expense items for editing GL Codes
    const medicalExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_medical',
        keyName: '_id',
    });

    const medicationsExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_medications',
        keyName: '_id',
    });

    const insuranceExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_insurance',
        keyName: '_id',
    });

    const careGivingExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_caregiving',
        keyName: '_id',
    });
    const physicalTherapyExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_physical_therapy',
        keyName: '_id',
    });
    const relocationLodgingExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_relocation_lodging',
        keyName: '_id',
    });
    const fundraisingExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_fundraising',
        keyName: '_id',
    });

    const otherExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_other',
        keyName: '_id',
    });

    const travelExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_travel',
        keyName: '_id',
    });

    const foodExpensesFieldArray = useFieldArray({
        control,
        name: 'transactions_food',
        keyName: '_id',
    });

    // Check if user has privileged roles that should have access to special features
    const hasPrivilegedRole = useCallback(() => {
        const privilegedRoles = ['AP Coordinator', 'Director of Finance'];

        // Check user roles
        const userRoles = user?.roles || [];
        if (userRoles.some((role) => privilegedRoles.includes(role))) {
            return true;
        }

        // Check person groups
        const personGroups = person?.groups || [];
        if (
            personGroups.some((group) => privilegedRoles.includes(group.name))
        ) {
            return true;
        }

        // Check person roles
        const personRoles = person?.roles || [];
        if (personRoles.some((role) => privilegedRoles.includes(role))) {
            return true;
        }

        return false;
    }, [user?.roles, person?.groups, person?.roles]);

    // Check if user is the assigned financial coordinator
    const isAssignedFC =
        parsedUser?.id === fundRequest?.financial_coordinator?.user_id;

    // Check if user has special roles that can edit regardless of assignment
    // Use the updated hasPrivilegedRole function to ensure we have latest user data
    const hasSpecialEditRole = hasPrivilegedRole();

    const isUserEditable = isAssignedFC || hasSpecialEditRole;

    // Define statuses that cannot be edited (excluding draft as requested)
    const nonEditableStatuses = ['in_mip'];
    const isStatusEditable = !nonEditableStatuses.includes(
        fundRequest?.fund_request_status?.value,
    );

    const isEditable = isUserEditable && isStatusEditable;

    // Add a watch for description to track character count
    const description = watch('description') || '';
    const descriptionLength = description.length;
    const maxDescriptionLength = 60;
    const maxInvoiceNumberLength = 20;

    // Calculate dynamic description length based on fund request ID
    const dynamicMaxDescriptionLength = fundRequest?.id
        ? maxDescriptionLength - (fundRequest.id.toString().length + 1)
        : maxDescriptionLength;

    const getFundRequest = async () => {
        if (!id) return;
        const resp = await fetchFundRequestDetail(id);
        handleFetchedFundRequest(resp);
        // Use serializer-provided raw funds and approved totals to compute net available
        const raw = Number(resp?.available_balance_raw ?? 0);
        const approved = Number(resp?.approved_amounts_total ?? 0);
        const netAvailable = raw - approved;
        const requestAmount = resp?.request_amount || 0;
        const isInsufficientFunds = netAvailable < requestAmount;

        setRawAvailableFunds(raw);
        setApprovedPendingTotal(approved);
        setUnapprovedTotal(Number(resp?.unapproved_amounts_total ?? 0));
        setAvailableFunds(netAvailable);
        setHasInsufficientFunds(isInsufficientFunds);
        setFundsLoaded(true);
        return resp;
    };

    useQuery({
        queryKey: ['fundrequest', id],
        queryFn: getFundRequest,
        retry: false,
    });

    const getAnnualRates = async () => {
        const resp = await fetchAnnualRates();
        fundRequestDispatch({
            type: 'set_annual_rates',
            payload: resp,
        });
    };

    useEffect(() => {
        getAnnualRates();
        return () => {
            fundRequestDispatch({ type: 'reset_fund_request' });
        };
    }, []);

    // Track if user has been initialized to prevent repeated calls
    const [userInitialized, setUserInitialized] = useState(false);

    // Refresh user data when component mounts to ensure latest roles/permissions (only once)
    useEffect(() => {
        if (!userInitialized) {
            const initializeUser = async () => {
                try {
                    await refreshUser();
                    setUserInitialized(true);
                } catch (error) {
                    console.error(
                        'FundRequestDetail: Error refreshing user data:',
                        error,
                    );
                    setUserInitialized(true); // Set to true even on error to prevent retry loops
                }
            };

            initializeUser();
        }
    }, [refreshUser, userInitialized]);

    useEffect(() => {
        if (fundRequest) {
            const ntaf_id = patient_info?.cf_patient_ntaf_id || '';
            const ap_invoice_number = fundRequest.ap_invoice_number || ntaf_id;
            methods.setValue('ap_invoice_number', ap_invoice_number);
        }
    }, [fundRequest]);

    const handleExpenseItemClick = (
        expenseCategory: FundRequestExpenseTypes,
        formKey: string,
        shouldPreviewFirstDocument = false,
    ) => {
        setIsDrawerOpen(true);
        setSelectedExpenseCategory(expenseCategory);

        fundRequestDispatch({
            type: 'set_form_key',
            payload: formKey,
        });

        // If requested, automatically show the first document in preview
        if (shouldPreviewFirstDocument) {
            // Get the current expense item to access its uploads
            const currentItem = methods.getValues(formKey as any);
            if (currentItem?.uploads && currentItem.uploads.length > 0) {
                setPreviewUploads(currentItem.uploads);
                setPreviewSlideIndex(0); // Select first document
            }
        }
    };

    const handleDocumentClick = (slideIndex: number) => {
        // Get the current expense item to access its uploads
        if (formKey) {
            const currentItem = methods.getValues(formKey);
            if (currentItem?.uploads && currentItem.uploads.length > 0) {
                setPreviewUploads(currentItem.uploads);
                setPreviewSlideIndex(slideIndex);
            }
        }
    };

    const handleClosePreview = () => {
        setPreviewSlideIndex(null);
        setPreviewUploads([]);
    };

    const handleFileReceiptClick = (
        formKey: string,
        expenseCategory: FundRequestExpenseTypes,
        uploadIndex: number,
    ) => {
        // Handle ACH documents specially - open in new tab instead of drawer
        if (
            formKey === 'ach_document' &&
            fundRequest?.ach_request?.ach_document_url
        ) {
            window.open(fundRequest.ach_request.ach_document_url, '_blank');
            return;
        }

        setIsDrawerOpen(true);
        setSelectedExpenseCategory(expenseCategory);

        fundRequestDispatch({
            type: 'set_form_key',
            payload: formKey,
        });

        // Get the current expense item to access its uploads
        const currentItem = methods.getValues(formKey as any);
        if (currentItem?.uploads && currentItem.uploads.length > 0) {
            setPreviewUploads(currentItem.uploads);
            setPreviewSlideIndex(uploadIndex); // Select the specific document
        }
    };

    /**
     * Resets form state and UI when a fund request is fetched
     */
    const handleFetchedFundRequest = (
        resp: FundRequest,
        closeDrawer = true,
    ) => {
        // Step 1: Update notes state and sort in ascending order (oldest first, newest at bottom)
        const sortedNotes = (resp.notes || []).sort((a, b) => {
            const ad = new Date(a.created_date).getTime();
            const bd = new Date(b.created_date).getTime();
            if (ad !== bd) return ad - bd;
            return (a.id || 0) - (b.id || 0);
        });
        setNotes(sortedNotes);

        // Step 2: Determine payment type and reset form
        const defaultPaymentType = determineDefaultPaymentType(
            resp.vendor,
            resp.payment_type,
        );

        // Transform food transactions to ensure recipient_type is a string
        const transformedFoodTransactions =
            resp.transactions_food?.map((transaction) => ({
                ...transaction,
                recipient_type:
                    typeof transaction.recipient_type === 'object' &&
                    transaction.recipient_type !== null
                        ? (transaction.recipient_type as any).value ||
                          transaction.recipient_type
                        : transaction.recipient_type,
            })) || [];

        reset({
            ...resp,
            payment_type: defaultPaymentType,
            transactions_food: transformedFoodTransactions,
        });

        // Step 3: Collect and set flagged comments
        const combinedDefaultTransactions = combineAllDefaultTransactions(resp);
        const flaggedComments = collectFlaggedComments(
            combinedDefaultTransactions,
            resp.transactions_travel || [],
            resp.transactions_food || [],
        );
        setFlaggedComments(flaggedComments);

        // Step 4: Update global fund request state
        fundRequestDispatch({
            type: 'set_fund_request',
            payload: resp,
        });

        // Step 5: Reset UI state
        setLoading(false);
        setSaving(false);

        if (isDrawerOpen && closeDrawer) {
            setIsDrawerOpen(false);
        }

        setDeleteExpenseItem(undefined);
    };

    const getCategories = async () => {
        const resp = await fetchFundRequestCategories();
        setCategories(resp);
        return resp;
    };

    const getHistory = async () => {
        if (!id) return;
        const resp = await fetchFundRequestHistory(id);
        // New API returns history items directly (no serialized_data wrapper)
        setRequestHistory(resp);
        return resp;
    };

    const getFinancialCoordinators = async () => {
        const resp = await fetchFinancialCoordinators();
        setFinancialCoordinators(resp.results);
        return resp.results;
    };

    const handleSaveFinancialCoordinator = async (
        fc: FinancialCoordinatorOption,
    ) => {
        try {
            if (!id) return;
            setSaving(true);
            const resp = await assignFinancialCoordinator(id, fc.id);
            handleFetchedFundRequest(resp);
            toast.success('Financial Coordinator assigned successfully');
        } catch (error) {
            // Properly handle the error type
            if (error instanceof Error) {
                toast.error(error.message);
            } else if (typeof error === 'string') {
                toast.error(error);
            } else {
                toast.error('An unknown error occurred');
            }
        }
    };

    const getGLCodes = async () => {
        const resp = await fetchFundRequestGLCodes();
        fundRequestDispatch({
            type: 'set_gl_codes',
            payload: resp,
        });
        setGlCodes(resp);
        return resp;
    };

    useQuery({
        queryKey: ['financialCoordinators'],
        queryFn: getFinancialCoordinators,
        retry: false,
    });

    useQuery({
        queryKey: ['categories'],
        queryFn: getCategories,
        retry: false,
    });

    useQuery({
        queryKey: ['glCodes'],
        queryFn: getGLCodes,
        retry: false,
    });

    const historyQuery = useQuery({
        queryKey: ['history', id],
        queryFn: getHistory,
        retry: false,
    });

    // Reset the one-time funds check state when navigating to a different request
    useEffect(() => {
        setInitialFundsChecked(false);
    }, [id]);

    // On initial load, check the cached funds via the lightweight endpoint
    // which refreshes server-side only if the cache is stale. Update local
    // funds state if the value changes. Show the spinner on the card while checking.
    useEffect(() => {
        const checkInitialFunds = async () => {
            const ntafId =
                fundRequest?.fund_info?.patient?.cf_patient_ntaf_id ||
                fundRequest?.fund_info?.cf_patient_ntaf_id;
            if (!ntafId || initialFundsChecked) return;
            setIsRefreshingFunds(true);
            try {
                const fundsResp = await fetchFundRequestAvailableBalance(
                    ntafId,
                );
                if (fundsResp && !fundsResp.error) {
                    const newRaw = Number(fundsResp.total || 0);
                    if (
                        Number.isFinite(newRaw) &&
                        newRaw !== rawAvailableFunds
                    ) {
                        const approved = approvedPendingTotal || 0;
                        const netAvailable = newRaw - approved;
                        const requestAmount = fundRequest?.request_amount || 0;
                        const isInsufficientFunds =
                            netAvailable < requestAmount;

                        setRawAvailableFunds(newRaw);
                        setAvailableFunds(netAvailable);
                        setHasInsufficientFunds(isInsufficientFunds);
                        setFundsLoaded(true);
                    }
                }
            } catch (e) {
                // Silent failure; initial load should not be blocked by funds check
                console.warn('Initial funds check failed', e);
            } finally {
                setIsRefreshingFunds(false);
                setInitialFundsChecked(true);
            }
        };
        checkInitialFunds();
        // We intentionally depend on fundRequest so the check runs when it becomes available
    }, [fundRequest, initialFundsChecked]);

    if (loading || !fundRequest) {
        return <div>Loading...</div>;
    }

    const patient_info = fundRequest.fund_info?.patient;
    const ntaf_id =
        patient_info?.cf_patient_ntaf_id ||
        fundRequest?.fund_info?.cf_patient_ntaf_id ||
        '';
    const totalExpenses =
        fundRequest.transactions_food.length +
        fundRequest.transactions_insurance.length +
        fundRequest.transactions_medical.length +
        fundRequest.transactions_medications.length +
        fundRequest.transactions_physical_therapy.length +
        fundRequest.transactions_caregiving.length +
        fundRequest.transactions_other.length +
        fundRequest.transactions_relocation_lodging.length +
        fundRequest.transactions_travel.length;

    const getDefaultExpensesTotal = (expenses: DefaultExpenseItem[]) => {
        let total = 0;
        expenses.forEach((e) => {
            total += Number(e.amount || 0);
        });

        return total;
    };

    const getTravelExpensesTotal = (expenses: TransactionTravel[]) => {
        let total = 0;
        expenses.forEach((e) => {
            total += Number(e.amount || 0);
        });

        return total;
    };

    const getFoodExpensesTotal = (expenses: TransactionFood[]) => {
        let total = 0;
        expenses.forEach((e) => {
            total += Number(e.amount || 0);
        });

        return total;
    };

    const handleSetNotes = (newNote: FundRequestNote) => {
        setNotes([newNote, ...notes]);
    };

    const getUseFullName = (obj: {
        first_name?: string;
        last_name?: string;
        middle_name?: string;
    }) => {
        return [obj.first_name, obj.middle_name, obj.last_name]
            .filter((name) => name !== undefined && name !== '')
            .join(' ');
    };

    const handleSave = async () => {
        if (!id) return;

        // Require submitted date before saving
        if (!fundRequest?.submitted_date) {
            toast.error('Please set a submitted date before saving');
            return;
        }

        // Check for form errors before continuing
        const errors = methods.formState.errors;
        if (Object.keys(errors).length > 0) {
            toast.error('Please fix form errors before saving');
            return;
        }

        setSaving(true);

        try {
            const data = methods.getValues();

            // Define the clear function to handle pending uploads
            const clearUploadsForFormKey = (formKeyToRemove: string) => {
                fundRequestDispatch({
                    type: 'clear_pending_uploads',
                    payload: formKeyToRemove,
                });
            };

            // Call saveFundRequest with success and error callbacks
            await saveFundRequest(
                id,
                data,
                formKey,
                pendingUploads as Record<string, PendingUpload[]>,
                categories,
                clearUploadsForFormKey,
                {
                    onSuccess: (updatedResp) => {
                        // Clear the tracked new item since it's now saved
                        setNewItemFormKey(null);
                        // Update fund request data and close the drawer
                        handleFetchedFundRequest(updatedResp, true);
                        toast.success('Saved successfully');
                    },
                    onError: (error) => {
                        console.error('Save error:', error);
                        toast.error('Error saving. Please try again.');
                    },
                },
            );
        } catch (error) {
            console.error('Save error:', error);
            toast.error('Error saving. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteItem = async ({
        formKey,
        expenseCategory,
    }: {
        formKey: string;
        expenseCategory: FundRequestExpenseTypes;
    }) => {
        try {
            // Get the field name and index from the formKey
            const match = formKey.match(/^(transactions_\w+)\[(\d+)\]$/);
            if (!match) {
                console.error('Invalid formKey format:', formKey);
                return;
            }

            const [, fieldName, indexStr] = match;
            const index = parseInt(indexStr, 10);

            // Get the item using fieldName and index
            const fieldValues = methods.getValues(fieldName as any);
            const item = fieldValues?.[index];

            if (!item?.id) return;

            setSaving(true);
            const resp = await deleteFundRequestItem(
                fundRequest.id,
                item.id,
                expenseCategory,
            );
            handleFetchedFundRequest(resp, false);
        } catch (error) {
            console.error('Error deleting item:', error);
            // Handle error
        }
    };

    const handleRevertPartialChild = async (child: FundRequest) => {
        try {
            setRevertingPartialChild(true);
            const resp = await revertPartialDisbursement(String(child.id));
            toast.success(
                'Partial disbursement undone; original amounts restored',
            );
            handleFetchedFundRequest(resp);
        } catch (error) {
            console.error(
                'Error reverting partial disbursement remainder:',
                error,
            );
            toast.error('Unable to remove remainder. Please try again.');
        } finally {
            setRevertingPartialChild(false);
            setRevertPartialChild(null);
        }
    };

    const handleFlagItem = async (
        item: ExpenseItem | undefined,
        item_type: FundRequestExpenseTypes | undefined,
        comment: string,
    ) => {
        setSaving(true);
        try {
            const resp = await addFundRequestItemFlag({
                item,
                item_type,
                comment,
            });
            handleFetchedFundRequest(resp);
        } catch (error) {
            console.error('Error flagging item:', error);
            if (error instanceof Error) {
                toast.error(error.message);
            } else if (typeof error === 'string') {
                toast.error(error);
            } else {
                toast.error('Failed to flag item. Please try again.');
            }
            setSaving(false);
        }
    };

    const handleDeleteFlag = async (
        item: ExpenseItem | undefined,
        item_type: FundRequestExpenseTypes | undefined,
    ) => {
        // Deleting flag just sets a resolved_date. It doesn't actually delete the flag.
        setSaving(true);
        try {
            const resp = await resolveFundRequestItemFlag({ item, item_type });
            handleFetchedFundRequest(resp, false);
        } catch (error) {
            console.error('Error resolving flag:', error);
            if (error instanceof Error) {
                toast.error(error.message);
            } else if (typeof error === 'string') {
                toast.error(error);
            } else {
                toast.error('Failed to resolve flag. Please try again.');
            }
            setSaving(false);
        }
    };

    const handleDeleteExpenseItem = async (
        item: ExpenseItem | undefined,
        item_type: FundRequestExpenseTypes | undefined,
    ) => {
        // Clear the flag from the expense item
        setSaving(true);
        try {
            const resp = await clearFundRequestItemFlag({ item, item_type });
            handleFetchedFundRequest(resp, false);
        } catch (error) {
            console.error('Error clearing flag from expense item:', error);
            if (error instanceof Error) {
                toast.error(error.message);
            } else if (typeof error === 'string') {
                toast.error(error);
            } else {
                toast.error(
                    'Failed to clear flag from expense item. Please try again.',
                );
            }
            setSaving(false);
        }
    };

    const handleNeedsAdditionalInfoSubmit = async (comment: string) => {
        if (!id) return;

        try {
            // Check if form is dirty and needs to be saved first
            if (methods.formState.isDirty && isEditable) {
                // Show toast for saving
                toast.loading('Saving fund request changes...', {
                    id: 'saving-changes',
                });

                // Save form first
                setSaving(true);
                try {
                    await handleSave();
                    toast.success('Changes saved successfully', {
                        id: 'saving-changes',
                    });
                } catch (error) {
                    toast.error('Failed to save changes', {
                        id: 'saving-changes',
                    });
                    console.error(
                        'Failed to save before status update:',
                        error,
                    );
                    setSaving(false);
                    return;
                }
            }

            // Show toast for status update
            toast.loading('Setting status to Needs Additional Info...', {
                id: 'status-update',
            });

            // Update status
            setSaving(true);
            const resp = await setStatusNeedsAdditionalInfo(id, comment);
            handleFetchedFundRequest(resp);
            toast.success('Fund Request updated', { id: 'status-update' });
        } catch (error) {
            console.error('Error updating to Needs Additional Info:', error);
            toast.error('Failed to update status', { id: 'status-update' });
        } finally {
            setSaving(false);
        }
    };

    const handleApReviewSubmit = async (priority: string) => {
        try {
            if (!id) return;

            // Check if form is dirty and needs to be saved first
            if (methods.formState.isDirty && isEditable) {
                // Show toast for saving
                toast.loading('Saving fund request changes...', {
                    id: 'saving-changes',
                });

                // Save form first
                setSaving(true);
                try {
                    await handleSave();
                    toast.success('Changes saved successfully', {
                        id: 'saving-changes',
                    });
                } catch (error) {
                    toast.error('Failed to save changes', {
                        id: 'saving-changes',
                    });
                    console.error(
                        'Failed to save before status update:',
                        error,
                    );
                    setSaving(false);
                    return;
                }
            }

            // Show toast for status update
            toast.loading('Setting status to AP Review...', {
                id: 'status-update',
            });

            // Update status
            setSaving(true);
            const resp = await setStatusApReview(id, priority);

            if (resp.error) {
                toast.error(resp.error, { id: 'status-update' });
            } else {
                handleFetchedFundRequest(resp);
                historyQuery.refetch();
                toast.success('Fund Request updated', { id: 'status-update' });
            }
        } catch (error) {
            toast.error('Failed to update status', { id: 'status-update' });
            if (error instanceof Error) {
                toast.error(error.message);
            } else if (typeof error === 'string') {
                toast.error(error);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleStatusUpdate = async (
        status: FundRequestStatus,
        note: string | null,
    ) => {
        try {
            if (!id) return;

            // Check if form is dirty and needs to be saved first
            if (methods.formState.isDirty && isEditable) {
                // Show toast for saving
                toast.loading('Saving fund request changes...', {
                    id: 'saving-changes',
                });

                // Save form first
                setSaving(true);
                try {
                    await handleSave();
                    toast.success('Changes saved successfully', {
                        id: 'saving-changes',
                    });
                } catch (error) {
                    toast.error('Failed to save changes', {
                        id: 'saving-changes',
                    });
                    console.error(
                        'Failed to save before status update:',
                        error,
                    );
                    setSaving(false);
                    return;
                }
                setSaving(false);
            }

            // Check if this is a Begin Review action and vendor address has never been used in a paid request
            if (
                status === FundRequestStatus.IN_REVIEW &&
                fundRequest?.fund_request_status?.value === 'submitted' &&
                fundRequest?.vendor_address &&
                fundRequest?.vendor_address?.address_last_paid === null
            ) {
                // Save the pending status update and show the address verification modal
                setPendingStatusUpdate({ status, note });
                setAddressVerificationModalOpen(true);
                return;
            }

            // Show toast for status update
            toast.loading('Updating status...', { id: 'status-update' });

            // Proceed with the update
            const result = await performStatusUpdate(status, note);

            if (result) {
                toast.success('Status updated successfully', {
                    id: 'status-update',
                });
            } else {
                toast.error('Failed to update status', { id: 'status-update' });
            }

            return result;
        } catch (error) {
            toast.error('Failed to update status', { id: 'status-update' });
            if (error instanceof Error) {
                toast.error(error.message);
            } else if (typeof error === 'string') {
                toast.error(error);
            }
            setSaving(false);
        }
    };

    // Function to perform the actual status update
    const performStatusUpdate = async (
        status: FundRequestStatus,
        note: string | null,
    ) => {
        setSaving(true);
        try {
            // Get current form values to include with status update
            const formData = methods.getValues();

            const resp = await setFundRequestStatus(id!, status, note, {
                ap_invoice_number: formData.ap_invoice_number,
                payment_type: formData.payment_type,
                needs_ach_info: formData.needs_ach_info,
                description: formData.description,
                address_does_not_match: formData.address_does_not_match,
            });
            if (resp.error) {
                toast.error(resp.error);
                setSaving(false);
                return null;
            }
            // Don't show success toast here since handleStatusUpdate will handle it
            handleFetchedFundRequest(resp);
            historyQuery.refetch();
            return resp;
        } catch (error) {
            console.error('Error in performStatusUpdate:', error);
            return null;
        } finally {
            setSaving(false);
        }
    };

    // Function to handle confirmation from the address verification modal
    const handleAddressVerificationProceed = async () => {
        if (pendingStatusUpdate) {
            // Show toast for status update
            toast.loading('Updating status...', { id: 'status-update' });

            const result = await performStatusUpdate(
                pendingStatusUpdate.status,
                pendingStatusUpdate.note,
            );

            if (result) {
                toast.success('Status updated successfully', {
                    id: 'status-update',
                });
            } else {
                toast.error('Failed to update status', { id: 'status-update' });
            }

            setAddressVerificationModalOpen(false);
            setPendingStatusUpdate(null);
        }
    };

    const handleAddToQueue = async () => {
        try {
            if (!id) return;
            setSaving(true);
            await addToQueue(id);
            toast.success('Added to queue');
            navigate('/fund-requests?status=ap_review');
        } catch (error) {
            if (error instanceof Error) {
                toast.error(error.message);
            } else if (typeof error === 'string') {
                toast.error(error);
            }
            setSaving(false);
        }
    };

    const handleSendReminder = async () => {
        try {
            if (!id) return;
            toast.loading('Sending reminder email...');
            const result = await sendNeedsInfoReminder(id);
            toast.dismiss();

            if (result.detail) {
                toast.error(result.detail);
                return;
            }

            toast.success(result.message || 'Reminder email sent successfully');

            // Refresh the fund request to get updated reminder dates
            const updatedFundRequest = await fetchFundRequestDetail(id);
            fundRequestDispatch({
                type: 'set_fund_request',
                payload: updatedFundRequest,
            });
        } catch (error) {
            toast.dismiss();
            console.error('Error sending reminder:', error);
            if (error instanceof Error) {
                toast.error(error.message);
            } else if (typeof error === 'string') {
                toast.error(error);
            } else {
                toast.error('Failed to send reminder email');
            }
        }
    };

    const handlePartialDisbursementSave = async (
        message: string,
    ): Promise<void> => {
        if (!id) throw new Error('Fund request ID is required');

        // Validate that message is not empty
        if (!message.trim()) {
            toast.error('Message cannot be empty');
            throw new Error('Message cannot be empty');
        }

        setSaving(true);

        try {
            // Call the API to suggest partial disbursement
            // This will set status to needs_info, partial_status to suggested, and add the message as a note
            const resp = await suggestPartialDisbursement(id, message.trim());

            // Update the local fund request data
            handleFetchedFundRequest(resp);

            // Show success message
            toast.success('Partial disbursement suggestion sent successfully');
        } catch (error) {
            console.error('Error suggesting partial disbursement:', error);
            if (error instanceof Error) {
                toast.error(error.message);
            } else if (typeof error === 'string') {
                toast.error(error);
            } else {
                toast.error('Failed to send partial disbursement suggestion');
            }
            throw error; // Re-throw to let the modal handle the error
        } finally {
            setSaving(false);
        }
    };

    const handleCreatePartialDisbursement = async (partialAmounts: any) => {
        try {
            if (!fundRequest?.id) {
                throw new Error('Fund request ID is required');
            }

            // Call the API service to create partial disbursement
            const data = await createPartialDisbursement(
                fundRequest.id.toString(),
                partialAmounts,
            );

            // Update the local fund request data
            handleFetchedFundRequest(data);

            toast.success('Partial disbursement created successfully');

            // Refresh the fund request data and history
            getFundRequest();
            historyQuery.refetch();
        } catch (error) {
            console.error('Error creating partial disbursement:', error);
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Failed to create partial disbursement';
            toast.error(errorMessage);
            throw error;
        }
    };

    const handleRefreshFunds = async () => {
        const ntaf_id =
            patient_info?.cf_patient_ntaf_id ||
            fundRequest?.fund_info?.cf_patient_ntaf_id;
        if (!ntaf_id) {
            toast.error('No NTAF ID available for fund refresh');
            return;
        }

        setIsRefreshingFunds(true);
        try {
            const fundsResp = await refreshFundRequestAvailableBalance(ntaf_id);

            if (fundsResp.error) {
                toast.error(fundsResp.error);
            } else {
                const newRaw = Number(fundsResp.total || 0);
                const approved = approvedPendingTotal || 0;
                const netAvailable = newRaw - approved;
                const requestAmount = fundRequest?.request_amount || 0;
                const isInsufficientFunds = netAvailable < requestAmount;

                setRawAvailableFunds(newRaw);
                setAvailableFunds(netAvailable);
                setHasInsufficientFunds(isInsufficientFunds);
                setFundsLoaded(true);
                toast.success('Funds refreshed successfully');
            }
        } catch (error) {
            console.error('Error refreshing funds:', error);
            if (error instanceof Error) {
                toast.error(`Failed to refresh funds: ${error.message}`);
            } else {
                toast.error('Failed to refresh funds');
            }
        } finally {
            setIsRefreshingFunds(false);
        }
    };

    const handleSelectCategory = (category: SelectOption | undefined) => {
        if (!category) return;

        // Find default GL code for this category (if available)
        const defaultGlCode =
            glCodes.find((c) => c.id === (category as any).default_gl_code)
                ?.value || '';

        // Define special expense type handlers
        if (category.value === 'food_incidentals') {
            // Handle food expenses
            const newIndex = foodExpensesFieldArray.fields.length;
            const newFormKey = `transactions_food[${newIndex}]`;

            foodExpensesFieldArray.append({
                recipient_type: null,
                recipient_name: '',
                start_date: '',
                end_date: '',
                amount: 0,
                gl_code: defaultGlCode,
            });

            setSelectedExpenseCategory(FundRequestExpenseTypes.FOOD);
            fundRequestDispatch({ type: 'set_form_key', payload: newFormKey });
            // Track this as a newly created item
            setNewItemFormKey(newFormKey);
            return;
        }

        if (category.value === 'travel_mileage') {
            // Handle travel expenses - set default GL code to Transportation (5002)
            const newIndex = travelExpensesFieldArray.fields.length;
            const newFormKey = `transactions_travel[${newIndex}]`;

            const newItem = travelSchema
                ? (buildTravelDefault(travelSchema, {
                      withGL: true,
                  }) as unknown as TransactionTravelSubmit)
                : ({
                      date_of_travel: '',
                      date_of_travel_end: '',
                      destination: '',
                      miles_driven: 0,
                      toll_amount: 0,
                      parking_amount: 0,
                      car_rental: 0,
                      airfare: 0,
                      rideshare: 0,
                      gl_code: '5002',
                      amount: 0,
                  } as unknown as TransactionTravelSubmit);

            travelExpensesFieldArray.append(newItem);

            setSelectedExpenseCategory(FundRequestExpenseTypes.TRAVEL);
            fundRequestDispatch({ type: 'set_form_key', payload: newFormKey });
            // Track this as a newly created item
            setNewItemFormKey(newFormKey);
            return;
        }

        // Map category values to their corresponding field arrays
        const categoryFieldArrayMap = {
            fundraising: fundraisingExpensesFieldArray,
            medical: medicalExpensesFieldArray,
            medications: medicationsExpensesFieldArray,
            caregiving: careGivingExpensesFieldArray,
            physical_therapy: physicalTherapyExpensesFieldArray,
            insurance: insuranceExpensesFieldArray,
            relocation_lodging: relocationLodgingExpensesFieldArray,
            other: otherExpensesFieldArray,
        };

        // Get the field array based on category value
        const fieldArray =
            categoryFieldArrayMap[
                category.value as keyof typeof categoryFieldArrayMap
            ];

        if (fieldArray) {
            // Create a new expense item of the default type
            const newIndex = fieldArray.fields.length;
            const formType = `transactions_${category.value}`;
            const newFormKey = `${formType}[${newIndex}]`;

            // Create the new expense item with category ID
            fieldArray.append({
                category: category.id,
                expense_date: '',
                end_date: '',
                amount: 0,
                description: '',
                gl_code: defaultGlCode,
            });

            setSelectedExpenseCategory(FundRequestExpenseTypes.DEFAULT);
            fundRequestDispatch({ type: 'set_form_key', payload: newFormKey });
            // Track this as a newly created item
            setNewItemFormKey(newFormKey);
        }
    };

    const expensesCategorySection = (
        formKey: string,
        fieldArray: {
            fields: DefaultExpenseItem[];
            remove: (index: number) => void;
        },
        title: string,
    ) => {
        if (!fundRequest || fieldArray.fields.length === 0) {
            return null; // Return nothing if no fields
        }
        const categoryTotal = toUSD(getDefaultExpensesTotal(fieldArray.fields));

        return (
            <section>
                <div className='mb-4 flex justify-between font-bold'>
                    <h2>{title}</h2>
                    <span>{categoryTotal}</span>
                </div>
                <div className='text-right text-xs font-bold'>GL Code</div>
                <div className='space-y-4'>
                    {fieldArray.fields.map((item, index) => {
                        return (
                            <ExpenseItemRow
                                key={`${formKey}_${index}`}
                                methods={methods}
                                formKey={`${formKey}[${index}]`}
                                description={item.description}
                                date={item.expense_date}
                                amount={item.amount}
                                glCode={item.gl_code}
                                glCodes={glCodes}
                                handleExpenseItemClick={handleExpenseItemClick}
                                expenseCategory={
                                    FundRequestExpenseTypes.DEFAULT
                                }
                                handleDeleteItem={(
                                    formKey,
                                    expenseCategory,
                                ) => {
                                    // Check for item.id or treat it as a new item
                                    if ('id' in item && item.id) {
                                        setDeleteExpenseItem({
                                            formKey,
                                            expenseCategory,
                                        });
                                    } else {
                                        fieldArray.remove(index);
                                    }
                                }}
                                isEditable={isEditable}
                            />
                        );
                    })}
                </div>
            </section>
        );
    };

    const hasStatusNote =
        fundRequest?.status_note &&
        fundRequest?.status_note?.fund_request_status?.value ===
            fundRequest?.fund_request_status?.value;

    let messageHeader = '';
    if (hasStatusNote) {
        const status = fundRequest?.fund_request_status?.value;
        if (status === 'appealed') {
            messageHeader = 'Plea from Client';
        } else {
            messageHeader = 'Message from Finance';
        }
    }

    // Determine reminder date and type for needs_info status
    // Priority: manual reminder (most recent) > second automated > first automated
    const getReminderInfo = () => {
        if (fundRequest?.fund_request_status?.value !== 'needs_info') {
            return { date: null, type: null };
        }

        const manualDate = fundRequest?.last_manual_reminder_sent_at;
        const secondDate = fundRequest?.needs_info_second_reminder_sent_at;
        const firstDate = fundRequest?.needs_info_reminder_sent_at;

        // Find the most recent date
        const dates = [
            { date: manualDate, type: 'manual' as const },
            { date: secondDate, type: 'second' as const },
            { date: firstDate, type: 'first' as const },
        ].filter((d) => d.date);

        if (dates.length === 0) {
            return { date: null, type: null };
        }

        // Sort by date descending (most recent first)
        dates.sort(
            (a, b) => new Date(b.date!).getTime() - new Date(a.date!).getTime(),
        );

        return { date: dates[0].date, type: dates[0].type };
    };

    const { date: reminderDate, type: reminderType } = getReminderInfo();

    return (
        <>
            <Breadcrumb
                pageName='Fund Request'
                parentPage='Fund Requests'
                parentPath={`/fund-requests${location.search}`}
                breadcrumbTitle={`${id}`}
            />
            <div className='mx-auto max-w-screen-2xl'>
                <FormProvider {...methods}>
                    <TitlePanel
                        isEditable={isEditable}
                        totalExpenses={totalExpenses}
                        handleSave={handleSave}
                        saving={saving}
                        handleNeedsAdditionalInfoSubmit={
                            handleNeedsAdditionalInfoSubmit
                        }
                        handleApReviewSubmit={handleApReviewSubmit}
                        flaggedComments={flaggedComments}
                        handleStatusUpdate={handleStatusUpdate}
                        handleAddToQueue={handleAddToQueue}
                        handleSendReminder={handleSendReminder}
                        reminderDate={reminderDate}
                        reminderType={reminderType}
                    />
                </FormProvider>
            </div>

            {fundRequest?.pending_change_request && (
                <div className='mx-auto mb-7.5 max-w-screen-2xl'>
                    <Panel addClasses='bg-[#FDEAEB] border-[#FDEAEB]'>
                        <div className='mb-4'>
                            <h2 className='text-[22px]/[28px] font-bold'>
                                There is a pending{' '}
                                <span
                                    className='cursor-pointer text-[#00AEB3] hover:opacity-80'
                                    onClick={() =>
                                        navigate(
                                            `/payees/request-changes/${fundRequest.pending_change_request}`,
                                        )
                                    }
                                >
                                    Change Request
                                </span>{' '}
                                for this Vendor
                            </h2>
                        </div>
                    </Panel>
                </div>
            )}

            <div className='mx-auto mb-7.5 max-w-screen-2xl'>
                <div className='col-span-12 grid grid-cols-1 gap-7.5 md:grid-cols-2 xl:grid-cols-4'>
                    <FundNameBlock
                        fundName={
                            fundRequest.fund_info?.fund_name ||
                            (patient_info?.person
                                ? `${patient_info.person.first_name} ${patient_info.person.last_name}`
                                : 'Unknown')
                        }
                        natafid={ntaf_id}
                        user_id={patient_info?.person?.user_id}
                        campaign_type={patient_info?.campaign_type}
                    />
                    <RequestAmountBlock
                        amount={fundRequest.request_amount}
                        availableFunds={availableFunds}
                        rawAvailableFunds={rawAvailableFunds}
                        approvedPendingTotal={approvedPendingTotal}
                        unapprovedTotal={unapprovedTotal}
                        hasInsufficientFunds={
                            availableFunds < (fundRequest?.request_amount || 0)
                        }
                        onRefreshFunds={handleRefreshFunds}
                        isRefreshing={isRefreshingFunds}
                    />
                    <FcBlock
                        fc={fundRequest.financial_coordinator}
                        financial_coordinators={financialCoordinators}
                        handleSaveFinancialCoordinator={
                            handleSaveFinancialCoordinator
                        }
                        saving={saving}
                    />
                    <CscBlock
                        csc={fundRequest.client_services_coordinator}
                        campaignId={fundRequest.campaign}
                    />
                </div>
            </div>

            {hasStatusNote && (
                <div className='5 mx-auto mb-7 max-w-screen-2xl'>
                    <Panel addClasses={`bg-[#e6f7f8] border-[#e6f7f8]`}>
                        <div className='xl:w-1/2'>
                            <h2 className='mb-4 text-2xl font-bold'>
                                {messageHeader}
                            </h2>
                            <p>{fundRequest?.status_note.note}</p>
                        </div>
                    </Panel>
                </div>
            )}

            <div className='mx-auto mt-7.5 grid max-w-screen-2xl grid-cols-12 gap-7.5'>
                <section className='col-span-12 xl:col-span-8'>
                    <div className='mb-7.5'>
                        <Panel>
                            {/* Header */}
                            <div className='mb-6 flex items-center justify-between'>
                                <h2 className='text-[22px]/[28px] font-bold'>
                                    Expenses
                                </h2>
                                <button
                                    className={`flex items-center gap-[8px] rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-200 hover:border-[#e6f7f8] hover:bg-[#e6f7f8] ${
                                        !isEditable
                                            ? 'cursor-not-allowed opacity-50'
                                            : ''
                                    }`}
                                    onClick={() => {
                                        setIsDrawerOpen(true);
                                        setSelectedExpenseCategory(undefined);
                                        fundRequestDispatch({
                                            type: 'reset_form_key',
                                        });
                                    }}
                                    disabled={!isEditable}
                                >
                                    <FaPlus className='text-[16px]' />
                                    ADD EXPENSE
                                </button>
                            </div>

                            {/* Expense Categories */}
                            <div className='space-y-8'>
                                {expensesCategorySection(
                                    'transactions_fundraising',
                                    fundraisingExpensesFieldArray,
                                    'Fundraising',
                                )}
                                {expensesCategorySection(
                                    'transactions_medical',
                                    medicalExpensesFieldArray,
                                    'Medical',
                                )}
                                {expensesCategorySection(
                                    'transactions_medications',
                                    medicationsExpensesFieldArray,
                                    'Medications',
                                )}
                                {expensesCategorySection(
                                    'transactions_caregiving',
                                    careGivingExpensesFieldArray,
                                    'Caregiving',
                                )}
                                {expensesCategorySection(
                                    'transactions_physical_therapy',
                                    physicalTherapyExpensesFieldArray,
                                    'Physical Therapy',
                                )}
                                {expensesCategorySection(
                                    'transactions_insurance',
                                    insuranceExpensesFieldArray,
                                    'Insurance',
                                )}
                                {expensesCategorySection(
                                    'transactions_relocation_lodging',
                                    relocationLodgingExpensesFieldArray,
                                    'Relocation/Lodging',
                                )}
                                {expensesCategorySection(
                                    'transactions_other',
                                    otherExpensesFieldArray,
                                    'Other',
                                )}

                                {/* Travel/Mileage Section */}
                                {fundRequest?.transactions_travel.length >
                                    0 && (
                                    <section>
                                        <div className='mb-4 flex justify-between font-bold'>
                                            <h2>Travel/Mileage</h2>
                                            <span>
                                                {toUSD(
                                                    getTravelExpensesTotal(
                                                        fundRequest?.transactions_travel,
                                                    ),
                                                )}
                                            </span>
                                        </div>
                                        {travelExpensesFieldArray.fields.map(
                                            (item, index) => (
                                                <ExpenseItemRow
                                                    isEditable={isEditable}
                                                    key={item._id || index}
                                                    methods={methods}
                                                    formKey={`transactions_travel[${index}]`}
                                                    description={
                                                        item.destination
                                                    }
                                                    date={item.date_of_travel}
                                                    amount={item.amount}
                                                    glCode={item.gl_code}
                                                    glCodes={glCodes}
                                                    handleExpenseItemClick={
                                                        handleExpenseItemClick
                                                    }
                                                    expenseCategory={
                                                        FundRequestExpenseTypes.TRAVEL
                                                    }
                                                    handleDeleteItem={(
                                                        formKey,
                                                        expenseCategory,
                                                    ) => {
                                                        if (item._id) {
                                                            setDeleteExpenseItem(
                                                                {
                                                                    formKey,
                                                                    expenseCategory,
                                                                },
                                                            );
                                                        } else {
                                                            travelExpensesFieldArray.remove(
                                                                index,
                                                            );
                                                        }
                                                    }}
                                                />
                                            ),
                                        )}
                                    </section>
                                )}

                                {/* Food/Incidentals Section */}
                                {fundRequest?.transactions_food.length > 0 && (
                                    <section>
                                        <div className='mb-4 flex justify-between font-bold'>
                                            <h2>Food/Incidentals</h2>
                                            <span>
                                                {toUSD(
                                                    getFoodExpensesTotal(
                                                        fundRequest?.transactions_food,
                                                    ),
                                                )}
                                            </span>
                                        </div>

                                        {foodExpensesFieldArray.fields.map(
                                            (item, index) => (
                                                <ExpenseItemRow
                                                    isEditable={isEditable}
                                                    key={item._id}
                                                    methods={methods}
                                                    formKey={`transactions_food[${index}]`}
                                                    description={
                                                        item.recipient_name
                                                    }
                                                    date={item.start_date}
                                                    amount={item.amount}
                                                    glCode={item.gl_code}
                                                    glCodes={glCodes}
                                                    handleExpenseItemClick={
                                                        handleExpenseItemClick
                                                    }
                                                    expenseCategory={
                                                        FundRequestExpenseTypes.FOOD
                                                    }
                                                    handleDeleteItem={(
                                                        formKey,
                                                        expenseCategory,
                                                    ) =>
                                                        setDeleteExpenseItem({
                                                            formKey,
                                                            expenseCategory,
                                                        })
                                                    }
                                                />
                                            ),
                                        )}
                                    </section>
                                )}
                                {/* Total */}
                                {fundRequest?.partial_children &&
                                fundRequest.partial_children.length > 0 ? (
                                    // Show partial disbursement breakdown when viewing parent with children
                                    <div className='space-y-3'>
                                        <div className='flex justify-between'>
                                            <h2 className='self-end font-medium'>
                                                TOTAL for disbursement
                                            </h2>
                                            <h1 className='text text-2xl font-medium'>
                                                {toUSD(
                                                    Number(
                                                        fundRequest?.request_amount ||
                                                            0,
                                                    ) +
                                                        (fundRequest.partial_children?.reduce(
                                                            (
                                                                sum: number,
                                                                child: FundRequest,
                                                            ) =>
                                                                sum +
                                                                Number(
                                                                    child.request_amount ||
                                                                        0,
                                                                ),
                                                            0,
                                                        ) || 0),
                                                )}
                                            </h1>
                                        </div>
                                        <div className='flex justify-between'>
                                            <h2 className='self-end font-bold'>
                                                PARTIAL disbursement
                                            </h2>
                                            <h1 className='text text-2xl font-bold'>
                                                {toUSD(
                                                    Number(
                                                        fundRequest?.request_amount ||
                                                            0,
                                                    ),
                                                )}
                                            </h1>
                                        </div>
                                        {fundRequest.partial_children.map(
                                            (child: FundRequest) => (
                                                <div
                                                    key={child.id}
                                                    className='flex items-center justify-between'
                                                    style={{
                                                        backgroundColor:
                                                            '#e6f7f8',
                                                        padding: '8px',
                                                        borderRadius: '8px',
                                                    }}
                                                >
                                                    <h2 className='font-medium'>
                                                        REMAINDER for
                                                        disbursement (
                                                        <Link
                                                            to={`/fund-requests/${child.id}`}
                                                            className='font-medium hover:underline'
                                                            style={{
                                                                color: '#00AEB3',
                                                            }}
                                                            onMouseEnter={(
                                                                e,
                                                            ) => {
                                                                e.currentTarget.style.color =
                                                                    '#008A8E';
                                                            }}
                                                            onMouseLeave={(
                                                                e,
                                                            ) => {
                                                                e.currentTarget.style.color =
                                                                    '#00AEB3';
                                                            }}
                                                        >
                                                            Request ID{' '}
                                                            {child.ap_invoice_number ||
                                                                child.id}
                                                        </Link>
                                                        )
                                                    </h2>
                                                    <div className='flex items-center gap-3'>
                                                        <button
                                                            className='rounded-full border-[2px] px-[18px] py-[6px] text-[12px]/[16px] font-bold uppercase transition-colors duration-200'
                                                            style={{
                                                                backgroundColor:
                                                                    '#00AEB3',
                                                                borderColor:
                                                                    '#00AEB3',
                                                                color: 'white',
                                                            }}
                                                            onMouseEnter={(
                                                                e,
                                                            ) => {
                                                                e.currentTarget.style.backgroundColor =
                                                                    '#008A8E';
                                                                e.currentTarget.style.borderColor =
                                                                    '#008A8E';
                                                            }}
                                                            onMouseLeave={(
                                                                e,
                                                            ) => {
                                                                e.currentTarget.style.backgroundColor =
                                                                    '#00AEB3';
                                                                e.currentTarget.style.borderColor =
                                                                    '#00AEB3';
                                                            }}
                                                            onClick={() =>
                                                                setRevertPartialChild(
                                                                    child,
                                                                )
                                                            }
                                                            disabled={
                                                                revertingPartialChild
                                                            }
                                                        >
                                                            {revertingPartialChild &&
                                                            revertPartialChild?.id ===
                                                                child.id
                                                                ? 'Undoing...'
                                                                : 'Undo Partial'}
                                                        </button>
                                                        <h1 className='text text-2xl font-medium'>
                                                            {toUSD(
                                                                Number(
                                                                    child.request_amount ||
                                                                        0,
                                                                ),
                                                            )}
                                                        </h1>
                                                    </div>
                                                </div>
                                            ),
                                        )}
                                    </div>
                                ) : fundRequest?.partial_parent ? (
                                    // Show partial disbursement breakdown when viewing a remainder (child) fund request
                                    <div className='space-y-3'>
                                        <div className='flex justify-between'>
                                            <h2 className='self-end font-medium'>
                                                TOTAL for disbursement
                                            </h2>
                                            <h1 className='text text-2xl font-medium'>
                                                {toUSD(
                                                    Number(
                                                        fundRequest
                                                            ?.partial_parent
                                                            ?.request_amount ||
                                                            0,
                                                    ) +
                                                        Number(
                                                            fundRequest?.request_amount ||
                                                                0,
                                                        ),
                                                )}
                                            </h1>
                                        </div>
                                        <div className='flex justify-between'>
                                            <h2 className='self-end font-bold'>
                                                PARTIAL disbursement
                                            </h2>
                                            <h1 className='text text-2xl font-bold'>
                                                {toUSD(
                                                    Number(
                                                        fundRequest?.request_amount ||
                                                            0,
                                                    ),
                                                )}
                                            </h1>
                                        </div>
                                        <div
                                            className='flex items-center justify-between'
                                            style={{
                                                backgroundColor: '#e6f7f8',
                                                padding: '8px',
                                                borderRadius: '8px',
                                            }}
                                        >
                                            <h2 className='font-medium'>
                                                Original disbursement (
                                                <Link
                                                    to={`/fund-requests/${fundRequest.partial_parent.id}`}
                                                    className='font-medium hover:underline'
                                                    style={{ color: '#00AEB3' }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.color =
                                                            '#008A8E';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.color =
                                                            '#00AEB3';
                                                    }}
                                                >
                                                    Request ID{' '}
                                                    {fundRequest.partial_parent
                                                        .ap_invoice_number ||
                                                        fundRequest
                                                            .partial_parent.id}
                                                </Link>
                                                )
                                            </h2>
                                            <h1 className='text text-2xl font-medium'>
                                                {toUSD(
                                                    Number(
                                                        fundRequest
                                                            ?.partial_parent
                                                            ?.request_amount ||
                                                            0,
                                                    ),
                                                )}
                                            </h1>
                                        </div>
                                    </div>
                                ) : (
                                    // Show normal total
                                    <div className='flex justify-between'>
                                        <h2 className='self-end font-bold'>
                                            TOTAL for disbursement
                                        </h2>
                                        <h1 className='text text-2xl font-bold'>
                                            {toUSD(
                                                Number(
                                                    fundRequest?.request_amount ||
                                                        0,
                                                ),
                                            )}
                                        </h1>
                                    </div>
                                )}

                                {/* Insufficient Funds Warning */}
                                {fundsLoaded &&
                                    availableFunds <
                                        (fundRequest?.request_amount || 0) &&
                                    fundRequest?.partial_status !==
                                        'accepted' &&
                                    (!fundRequest?.partial_children ||
                                        fundRequest.partial_children.length ===
                                            0) &&
                                    !fundRequest?.partial_parent && (
                                        <div
                                            className='mt-4 rounded-lg border-[2px] p-4'
                                            style={{
                                                backgroundColor: '#fdeaeb',
                                                borderColor: '#fdeaeb',
                                            }}
                                        >
                                            <div className='mb-3 flex items-center gap-2'>
                                                <FaExclamationTriangle
                                                    style={{ color: '#ef4444' }}
                                                    size={18}
                                                />
                                                <h3
                                                    className='text-[18px]/[22px] font-bold uppercase'
                                                    style={{ color: '#dc2626' }}
                                                >
                                                    Insufficient Funds
                                                </h3>
                                            </div>
                                            <p
                                                className='mb-3'
                                                style={{ color: '#7f1d1d' }}
                                            >
                                                The requested amount (
                                                {toUSD(
                                                    Number(
                                                        fundRequest?.request_amount ||
                                                            0,
                                                    ),
                                                )}
                                                ) exceeds the available funds (
                                                {toUSD(availableFunds)}).
                                            </p>
                                            {fundRequest?.partial_status ===
                                            'suggested' ? (
                                                <button
                                                    className='cursor-not-allowed rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase opacity-60'
                                                    style={{
                                                        backgroundColor:
                                                            '#6b7280',
                                                        borderColor: '#6b7280',
                                                        color: 'white',
                                                    }}
                                                    disabled
                                                >
                                                    Partial Disbursement
                                                    Suggested
                                                </button>
                                            ) : (
                                                <button
                                                    className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-200'
                                                    style={{
                                                        backgroundColor:
                                                            '#dc2626',
                                                        borderColor: '#dc2626',
                                                        color: 'white',
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor =
                                                            '#b91c1c';
                                                        e.currentTarget.style.borderColor =
                                                            '#b91c1c';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor =
                                                            '#dc2626';
                                                        e.currentTarget.style.borderColor =
                                                            '#dc2626';
                                                    }}
                                                    onClick={() =>
                                                        setPartialDisbursementModalOpen(
                                                            true,
                                                        )
                                                    }
                                                >
                                                    Suggest Partial Disbursement
                                                </button>
                                            )}
                                        </div>
                                    )}

                                {/* Partial Disbursement Accepted */}
                                {fundRequest?.partial_status === 'accepted' &&
                                    (!fundRequest?.partial_children ||
                                        fundRequest.partial_children.length ===
                                            0) && (
                                        <div
                                            className='mt-4 rounded-lg border-[2px] p-4'
                                            style={{
                                                backgroundColor: '#F0FDF4',
                                                borderColor: '#BBF7D0',
                                            }}
                                        >
                                            <div className='flex items-center justify-between'>
                                                <div className='flex items-center gap-3'>
                                                    <FaCheck
                                                        style={{
                                                            color: '#16A34A',
                                                        }}
                                                        size={20}
                                                    />
                                                    <h3
                                                        className='text-[16px]/[20px] font-bold uppercase tracking-wide'
                                                        style={{
                                                            color: '#166534',
                                                        }}
                                                    >
                                                        Client Accepted Partial
                                                        Disbursement
                                                    </h3>
                                                </div>
                                                <button
                                                    className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase'
                                                    style={{
                                                        backgroundColor:
                                                            'transparent',
                                                        borderColor: '#166534',
                                                        color: '#166534',
                                                    }}
                                                    onClick={() =>
                                                        setCreatePartialDisbursementModalOpen(
                                                            true,
                                                        )
                                                    }
                                                >
                                                    Partial Disbursement
                                                </button>
                                            </div>
                                        </div>
                                    )}
                            </div>
                        </Panel>
                    </div>

                    <div className='mb-7.5'>
                        <FormProvider {...methods}>
                            <Payee
                                isEditable={isEditable}
                                onVendorVerified={historyQuery.refetch}
                                onVerifyingChange={setSaving}
                            />
                        </FormProvider>
                    </div>

                    <div className='bg-[#E6F7F8] p-5'>
                        <FormProvider {...methods}>
                            <div>
                                <h2 className='mb-5 text-[22px]/[28px] font-bold'>
                                    Internal Only
                                </h2>

                                <div className='space-y-4'>
                                    <div className='space-y-2'>
                                        <label
                                            htmlFor='description'
                                            className='block text-sm font-medium text-gray-700'
                                        >
                                            Description
                                        </label>
                                        <div className='relative'>
                                            <textarea
                                                {...methods.register(
                                                    'description',
                                                    {
                                                        maxLength: {
                                                            value: dynamicMaxDescriptionLength,
                                                            message: `Description cannot exceed ${dynamicMaxDescriptionLength} characters (60 - (ID length + 1))`,
                                                        },
                                                    },
                                                )}
                                                className='focus:ring-blue-500 min-h-[200px] w-full rounded-md border border-gray-200 bg-white p-2 focus:outline-none focus:ring-2'
                                                maxLength={
                                                    dynamicMaxDescriptionLength
                                                }
                                            />
                                            <div className='mt-1 flex justify-between text-sm'>
                                                <span
                                                    className={`${
                                                        descriptionLength >=
                                                        dynamicMaxDescriptionLength
                                                            ? 'text-red-500'
                                                            : 'text-gray-500'
                                                    }`}
                                                >
                                                    {descriptionLength}/
                                                    {
                                                        dynamicMaxDescriptionLength
                                                    }{' '}
                                                    characters
                                                </span>
                                                {methods.formState.errors
                                                    .description && (
                                                    <span className='text-red-500'>
                                                        {
                                                            methods.formState
                                                                .errors
                                                                .description
                                                                .message as string
                                                        }
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className='space-y-2'>
                                        <label
                                            htmlFor='ap_invoice_number'
                                            className='block text-sm font-medium text-gray-700'
                                        >
                                            PF Invoice Number{' '}
                                            <span className='text-red-500'>
                                                *
                                            </span>
                                        </label>
                                        <div className='relative'>
                                            <input
                                                {...methods.register(
                                                    'ap_invoice_number',
                                                    {
                                                        maxLength: {
                                                            value: maxInvoiceNumberLength,
                                                            message: `Invoice number cannot exceed ${maxInvoiceNumberLength} characters`,
                                                        },
                                                    },
                                                )}
                                                className='focus:ring-blue-500 w-full rounded-md border border-gray-200 bg-white p-2 focus:outline-none focus:ring-2'
                                                placeholder='XXXXXXXXXX'
                                                maxLength={
                                                    maxInvoiceNumberLength
                                                }
                                            />
                                            {methods.formState.errors
                                                .ap_invoice_number && (
                                                <span className='text-red-500 mt-1 block text-sm'>
                                                    {
                                                        methods.formState.errors
                                                            .ap_invoice_number
                                                            .message as string
                                                    }
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </FormProvider>
                    </div>
                </section>

                <div className='col-span-12 xl:col-span-4'>
                    {fundRequest?.fund_request_status?.label ===
                        'AP Review' && (
                        <div className='mb-7.5'>
                            <Panel addClasses={'bg-white border-stroke !p-4'}>
                                <HistoricalExpenses />
                            </Panel>
                        </div>
                    )}
                    <Panel addClasses={'bg-white border-stroke !p-4'}>
                        <Tabs
                            openTab={openTab}
                            setOpenTab={setOpenTab}
                            tabNames={tabNames}
                            className='!mb-2 justify-around'
                            tabClassName='!px-3'
                        >
                            {openTab === 1 && (
                                <div className='overflow-hidden p-3'>
                                    <div className='mb-4 flex flex-wrap items-center justify-between gap-2'>
                                        <h2 className='text-2xl font-bold'>
                                            History ({requestHistory.length})
                                        </h2>
                                        {requestHistory.length === 0 && (
                                            <div className='flex flex-wrap gap-2'>
                                                <button className='flex items-center gap-2 whitespace-nowrap rounded-full bg-[#F4C430] px-4 py-2 text-sm font-bold uppercase'>
                                                    <FaPlus className='text-xs' />
                                                    ADD ATTEMPT
                                                </button>
                                                <button className='flex items-center gap-2 whitespace-nowrap rounded-full border-2 border-black px-4 py-2 text-sm font-bold uppercase'>
                                                    <FaPlus className='text-xs' />
                                                    ADD NOTE
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {requestHistory.length === 0 ? (
                                        <p className='py-8 text-center text-gray-500'>
                                            No history found. Add the first
                                            entry!
                                        </p>
                                    ) : (
                                        <div className='space-y-4'>
                                            {requestHistory.map(
                                                (item, index) => {
                                                    // Use action field if available, otherwise fallback to status
                                                    let action =
                                                        item?.action ||
                                                        item?.status;
                                                    if (
                                                        !item?.action &&
                                                        item?.status ===
                                                            'Submitted'
                                                    ) {
                                                        action = `Request ${item?.status}`;
                                                    } else if (
                                                        !item?.action &&
                                                        item?.status ===
                                                            'In Review'
                                                    ) {
                                                        action =
                                                            'Review started';
                                                    }
                                                    return (
                                                        <div
                                                            key={index}
                                                            className='flex flex-col'
                                                        >
                                                            <div className='flex items-center gap-2 text-sm text-gray-5'>
                                                                <span>
                                                                    {formatDateIfValid(
                                                                        item?.updated_date,
                                                                        'MM/dd/yy h:mm a',
                                                                    )}
                                                                </span>
                                                                {item?.duration_days !=
                                                                    null && (
                                                                    <span className='rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600'>
                                                                        {Number(
                                                                            item.duration_days,
                                                                        ) < 1
                                                                            ? `${Math.round(
                                                                                  Number(
                                                                                      item.duration_days,
                                                                                  ) *
                                                                                      24,
                                                                              )}h`
                                                                            : `${Number(
                                                                                  item.duration_days,
                                                                              )}d`}
                                                                    </span>
                                                                )}
                                                                {(item?.fund_request_snapshot ||
                                                                    item?.changes) && (
                                                                    <button
                                                                        onClick={() =>
                                                                            setLogEntryModal(
                                                                                {
                                                                                    isOpen: true,
                                                                                    data:
                                                                                        item.fund_request_snapshot ||
                                                                                        item.changes ||
                                                                                        null,
                                                                                },
                                                                            )
                                                                        }
                                                                        className='text-gray-400 transition-colors hover:text-[#00AEB3]'
                                                                        title={
                                                                            item
                                                                                ?.fund_request_snapshot
                                                                                ?.mip_payload
                                                                                ? 'View MIP payload'
                                                                                : item?.fund_request_snapshot
                                                                                ? 'View fund request snapshot'
                                                                                : 'View changes'
                                                                        }
                                                                    >
                                                                        <HiOutlineInformationCircle className='h-4 w-4' />
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <div>
                                                                {action} by{' '}
                                                                {item?.user_id ? (
                                                                    <Link
                                                                        to={`/users/${item?.user_id}`}
                                                                        className='font-bold text-[#00AEB3]'
                                                                    >
                                                                        {
                                                                            item?.person_name
                                                                        }
                                                                    </Link>
                                                                ) : (
                                                                    <span className='font-bold'>
                                                                        {
                                                                            item?.person_name
                                                                        }
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {item?.description && (
                                                                <div className='mt-1 text-sm text-gray-600'>
                                                                    {
                                                                        item.description
                                                                    }
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                },
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            {openTab === 2 && (
                                <FilesTab
                                    files={extractUploads(fundRequest)}
                                    onReceiptClick={handleFileReceiptClick}
                                    fundRequestId={fundRequest?.id || id}
                                />
                            )}
                            {openTab === 3 && (
                                <>
                                    <div className='mb-6 mt-5 flex items-center justify-between'>
                                        <h2 className='mb-4 text-2xl font-bold'>{`Notes (${notes.length})`}</h2>
                                    </div>

                                    {/* Teal scrollbar styling for notes list */}
                                    <style>
                                        {`
                                        .notes-scroll { scrollbar-color: #00AEB3 #E5F7F7; }
                                        .notes-scroll::-webkit-scrollbar { width: 8px; }
                                        .notes-scroll::-webkit-scrollbar-track { background: #E5F7F7; border-radius: 8px; }
                                        .notes-scroll::-webkit-scrollbar-thumb { background-color: #00AEB3; border-radius: 8px; border: 2px solid #E5F7F7; }
                                        .notes-scroll p { word-break: break-word; }
                                        `}
                                    </style>
                                    <div
                                        ref={notesListRef}
                                        className='notes-scroll'
                                        style={{
                                            maxHeight: 320,
                                            overflowY: 'auto',
                                            paddingRight: 6,
                                        }}
                                    >
                                        {notes.length > 0 &&
                                            notes.map((note) => {
                                                return (
                                                    <div
                                                        className='mx-auto mt-5 max-w-md bg-white'
                                                        key={note.id}
                                                    >
                                                        <div className='mb-2 flex justify-between text-xs text-gray-5'>
                                                            <span>
                                                                {formatDateIfValid(
                                                                    note.created_date,
                                                                    'MM/dd/yy',
                                                                )}
                                                            </span>
                                                            <span className='flex items-center gap-2 font-bold'>
                                                                {getUseFullName(
                                                                    note.created_by,
                                                                )}
                                                                {!note.is_client_note && (
                                                                    <img
                                                                        src={
                                                                            LogoIcon
                                                                        }
                                                                        alt='HHL'
                                                                        className='inline-block'
                                                                        style={{
                                                                            width: 18,
                                                                            height: 18,
                                                                        }}
                                                                    />
                                                                )}
                                                                {!note.is_client_note ? (
                                                                    <button
                                                                        type='button'
                                                                        onClick={async () => {
                                                                            try {
                                                                                const updated =
                                                                                    await toggleFundRequestNoteInternal(
                                                                                        note.id,
                                                                                    );
                                                                                setNotes(
                                                                                    (
                                                                                        prev,
                                                                                    ) =>
                                                                                        prev.map(
                                                                                            (
                                                                                                n,
                                                                                            ) =>
                                                                                                n.id ===
                                                                                                note.id
                                                                                                    ? {
                                                                                                          ...n,
                                                                                                          is_internal:
                                                                                                              updated.is_internal,
                                                                                                      }
                                                                                                    : n,
                                                                                        ),
                                                                                );
                                                                            } catch (e) {
                                                                                console.error(
                                                                                    'Failed to toggle internal flag',
                                                                                    e,
                                                                                );
                                                                            }
                                                                        }}
                                                                        title={
                                                                            note.is_internal
                                                                                ? 'Click to make visible to client'
                                                                                : 'Click to mark as internal'
                                                                        }
                                                                        style={{
                                                                            fontSize: 10,
                                                                            fontWeight: 700,
                                                                            padding:
                                                                                '2px 8px',
                                                                            borderRadius: 999,
                                                                            backgroundColor:
                                                                                note.is_internal
                                                                                    ? '#FEBF3E'
                                                                                    : 'transparent',
                                                                            color: '#1F1F1F',
                                                                            border: note.is_internal
                                                                                ? '1px solid #FEBF3E'
                                                                                : '1px dashed #B0B0B0',
                                                                            textTransform:
                                                                                'uppercase',
                                                                            letterSpacing: 0.5,
                                                                            cursor: 'pointer',
                                                                        }}
                                                                    >
                                                                        Internal
                                                                    </button>
                                                                ) : (
                                                                    note.is_internal && (
                                                                        <span
                                                                            style={{
                                                                                fontSize: 10,
                                                                                fontWeight: 700,
                                                                                padding:
                                                                                    '2px 8px',
                                                                                borderRadius: 999,
                                                                                backgroundColor:
                                                                                    '#FEBF3E',
                                                                                color: '#1F1F1F',
                                                                                textTransform:
                                                                                    'uppercase',
                                                                                letterSpacing: 0.5,
                                                                            }}
                                                                        >
                                                                            Internal
                                                                        </span>
                                                                    )
                                                                )}
                                                            </span>
                                                        </div>
                                                        <div
                                                            className='rounded-lg p-4'
                                                            style={{
                                                                backgroundColor:
                                                                    note.is_client_note
                                                                        ? note.admin_read
                                                                            ? '#F5FAEB'
                                                                            : '#e9e9e9'
                                                                        : note.is_internal
                                                                        ? '#FFF5DC'
                                                                        : '#E5F7F7',
                                                                border:
                                                                    note.is_client_note &&
                                                                    note.admin_read
                                                                        ? '1px solid #AFC12D'
                                                                        : note.is_internal
                                                                        ? '1px solid #FEBF3E'
                                                                        : 'none',
                                                            }}
                                                        >
                                                            <p className='mb-2 text-sm leading-relaxed text-gray-600'>
                                                                {safeParseHtml(
                                                                    note?.note ||
                                                                        '',
                                                                )}
                                                            </p>
                                                            <div className='mt-1 flex items-center justify-end'>
                                                                {note.admin_read ? (
                                                                    <span
                                                                        className='flex items-center gap-1 text-sm'
                                                                        style={{
                                                                            color: '#AFC12D',
                                                                        }}
                                                                    >
                                                                        <MdCheck
                                                                            size={
                                                                                16
                                                                            }
                                                                        />{' '}
                                                                        Read
                                                                    </span>
                                                                ) : (
                                                                    (note.is_client_note ||
                                                                        note.is_internal) && (
                                                                        <button
                                                                            className='text-sm text-[#00AEB3] hover:underline'
                                                                            onClick={async () => {
                                                                                try {
                                                                                    await markFundRequestNoteRead(
                                                                                        note.id,
                                                                                    );
                                                                                    setNotes(
                                                                                        (
                                                                                            prev,
                                                                                        ) =>
                                                                                            prev.map(
                                                                                                (
                                                                                                    n,
                                                                                                ) =>
                                                                                                    n.id ===
                                                                                                    note.id
                                                                                                        ? {
                                                                                                              ...n,
                                                                                                              admin_read:
                                                                                                                  true,
                                                                                                          }
                                                                                                        : n,
                                                                                            ),
                                                                                    );
                                                                                } catch (e) {
                                                                                    console.error(
                                                                                        'Failed to mark note as read',
                                                                                        e,
                                                                                    );
                                                                                }
                                                                            }}
                                                                            title='Mark as read'
                                                                        >
                                                                            Mark
                                                                            as
                                                                            read
                                                                        </button>
                                                                    )
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>

                                    {/* Inline add note composer */}
                                    <div className='mx-auto mt-6 max-w-md bg-white'>
                                        <label className='mb-2 block text-sm font-medium text-gray-700'>
                                            Add a note
                                        </label>
                                        <textarea
                                            className='w-full rounded border border-stroke p-3 text-sm outline-none focus:border-primary'
                                            rows={3}
                                            placeholder='Type your note here...'
                                            value={newNote}
                                            onChange={(e) =>
                                                setNewNote(e.target.value)
                                            }
                                        />
                                        <label className='mt-2 flex cursor-pointer items-center gap-2 text-sm text-gray-700'>
                                            <input
                                                type='checkbox'
                                                className='h-4 w-4 rounded border-stroke text-primary focus:ring-primary'
                                                checked={newNoteIsInternal}
                                                disabled={sendingNote}
                                                onChange={(e) =>
                                                    setNewNoteIsInternal(
                                                        e.target.checked,
                                                    )
                                                }
                                            />
                                            <span>
                                                Internal only — not visible to
                                                client
                                            </span>
                                        </label>
                                        <div className='mt-3'>
                                            <button
                                                className='rounded bg-[#00AEB3] px-4 py-2 text-white disabled:opacity-50'
                                                disabled={
                                                    sendingNote ||
                                                    !newNote.trim()
                                                }
                                                onClick={async () => {
                                                    if (!newNote.trim()) return;
                                                    try {
                                                        setSendingNote(true);
                                                        const resp =
                                                            await addFundRequestNote(
                                                                newNote.trim(),
                                                                fundRequest.id,
                                                                newNoteIsInternal,
                                                            );
                                                        // Insert and keep ascending order
                                                        setNotes((prev) => {
                                                            const arr = [
                                                                ...prev,
                                                                resp,
                                                            ];
                                                            return arr.sort(
                                                                (a, b) => {
                                                                    const ad =
                                                                        new Date(
                                                                            a.created_date,
                                                                        ).getTime();
                                                                    const bd =
                                                                        new Date(
                                                                            b.created_date,
                                                                        ).getTime();
                                                                    if (
                                                                        ad !==
                                                                        bd
                                                                    )
                                                                        return (
                                                                            ad -
                                                                            bd
                                                                        );
                                                                    return (
                                                                        (a.id ||
                                                                            0) -
                                                                        (b.id ||
                                                                            0)
                                                                    );
                                                                },
                                                            );
                                                        });
                                                        setNewNote('');
                                                        setNewNoteIsInternal(
                                                            false,
                                                        );
                                                    } catch (e) {
                                                        console.error(
                                                            'Failed to add note',
                                                            e,
                                                        );
                                                    } finally {
                                                        setSendingNote(false);
                                                    }
                                                }}
                                            >
                                                {sendingNote
                                                    ? 'Sending...'
                                                    : 'Send'}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </Tabs>
                    </Panel>
                </div>
            </div>

            {/* Document Preview Area - Only show when drawer is open and preview is active */}
            {isDrawerOpen &&
                previewOpen &&
                previewUploads.length > 0 &&
                previewSlideIndex !== null && (
                    <div className='fixed bottom-0 left-0 right-[500px] top-0 z-[10000] bg-gray-100'>
                        <DocumentPreview
                            isOpen={previewOpen}
                            onClose={handleClosePreview}
                            uploads={previewUploads}
                            slideIndex={previewSlideIndex}
                        />
                    </div>
                )}

            {/* Drawer */}
            <FormProvider {...methods}>
                <ExpenseInfoDrawer
                    isOpen={isDrawerOpen}
                    setIsOpen={(open) => {
                        setIsDrawerOpen(open);
                        if (!open) {
                            // If closing the drawer, check if we have an unsaved new item to remove
                            if (newItemFormKey) {
                                // Parse the formKey to get field name and index
                                const match = newItemFormKey.match(
                                    /^(transactions_\w+)\[(\d+)\]$/,
                                );
                                if (match) {
                                    const [, fieldName, indexStr] = match;
                                    const index = parseInt(indexStr, 10);

                                    // Get the item to check if it has an id (saved) or not (unsaved)
                                    const fieldValues = methods.getValues(
                                        fieldName as any,
                                    );
                                    const item = fieldValues?.[index];

                                    // Only remove if the item exists and has no id (meaning it was never saved)
                                    if (item && !item.id) {
                                        // Find the appropriate field array and remove the item
                                        const fieldArrayMap: Record<
                                            string,
                                            any
                                        > = {
                                            transactions_food:
                                                foodExpensesFieldArray,
                                            transactions_travel:
                                                travelExpensesFieldArray,
                                            transactions_fundraising:
                                                fundraisingExpensesFieldArray,
                                            transactions_medical:
                                                medicalExpensesFieldArray,
                                            transactions_medications:
                                                medicationsExpensesFieldArray,
                                            transactions_caregiving:
                                                careGivingExpensesFieldArray,
                                            transactions_physical_therapy:
                                                physicalTherapyExpensesFieldArray,
                                            transactions_insurance:
                                                insuranceExpensesFieldArray,
                                            transactions_relocation_lodging:
                                                relocationLodgingExpensesFieldArray,
                                            transactions_other:
                                                otherExpensesFieldArray,
                                        };

                                        const fieldArray =
                                            fieldArrayMap[fieldName];
                                        if (fieldArray) {
                                            fieldArray.remove(index);
                                        }
                                    }
                                }

                                // Clear the tracked new item
                                setNewItemFormKey(null);
                            }

                            setSelectedExpenseCategory(undefined);
                            fundRequestDispatch({
                                type: 'reset_form_key',
                            });
                            // Also close the preview when drawer closes
                            handleClosePreview();
                        }
                    }}
                    categories={categories}
                    glCodes={glCodes}
                    handleSelectCategory={handleSelectCategory}
                    selectedExpenseCategory={selectedExpenseCategory}
                    onSubmit={handleSave}
                    handleFlagItem={handleFlagItem}
                    handleDeleteFlag={handleDeleteFlag}
                    handleDeleteExpenseItem={handleDeleteExpenseItem}
                    onDocumentClick={handleDocumentClick}
                    selectedDocumentIndex={previewSlideIndex ?? undefined}
                    saving={saving}
                />
            </FormProvider>

            {deleteExpenseItem && (
                <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'>
                    <div className='relative w-full max-w-xl rounded-lg bg-white shadow-lg'>
                        <button
                            className='absolute right-4 top-4 text-gray-600 hover:text-gray-900'
                            onClick={() => setDeleteExpenseItem(undefined)}
                        >
                            <RxCross2 />
                        </button>
                        <div className='p-8'>
                            <h2 className='mb-[30px] text-[22px] font-bold'>
                                Are you sure you want to delete this item?
                            </h2>
                            <div className='mt-5 flex justify-start space-x-4'>
                                <button
                                    className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                                    onClick={() =>
                                        setDeleteExpenseItem(undefined)
                                    }
                                >
                                    No, Cancel
                                </button>
                                <button
                                    className='rounded-full bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:bg-yellow'
                                    onClick={() => {
                                        handleDeleteItem(deleteExpenseItem);
                                    }}
                                    disabled={saving}
                                >
                                    {saving ? 'Deleting...' : 'Yes, Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {revertPartialChild && (
                <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'>
                    <div className='relative w-full max-w-xl rounded-lg bg-white shadow-lg'>
                        <button
                            className='absolute right-4 top-4 text-gray-600 hover:text-gray-900'
                            onClick={() => setRevertPartialChild(null)}
                        >
                            <RxCross2 />
                        </button>
                        <div className='p-8'>
                            <h2 className='mb-4 text-[22px] font-bold'>
                                Undo partial disbursement?
                            </h2>
                            <p className='mb-6 text-sm text-gray-600'>
                                This will delete Fund Request #
                                {revertPartialChild.ap_invoice_number ||
                                    revertPartialChild.id}{' '}
                                and restore the original amounts on the parent
                                request.
                            </p>
                            <div className='mt-5 flex justify-start space-x-4'>
                                <button
                                    className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                                    onClick={() => setRevertPartialChild(null)}
                                    disabled={revertingPartialChild}
                                >
                                    No, Cancel
                                </button>
                                <button
                                    className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-200'
                                    style={{
                                        backgroundColor: '#dc2626',
                                        borderColor: '#dc2626',
                                        color: 'white',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                            '#b91c1c';
                                        e.currentTarget.style.borderColor =
                                            '#b91c1c';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                            '#dc2626';
                                        e.currentTarget.style.borderColor =
                                            '#dc2626';
                                    }}
                                    onClick={() =>
                                        handleRevertPartialChild(
                                            revertPartialChild,
                                        )
                                    }
                                    disabled={revertingPartialChild}
                                >
                                    {revertingPartialChild
                                        ? 'Undoing...'
                                        : 'Yes, Undo'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Address Verification Modal */}
            <AddressVerificationModal
                isOpen={addressVerificationModalOpen}
                onClose={() => {
                    setAddressVerificationModalOpen(false);
                    setPendingStatusUpdate(null);
                }}
                onProceed={handleAddressVerificationProceed}
                address={fundRequest?.vendor_address}
                vendorName={fundRequest?.vendor?.name}
                ntafId={patient_info?.cf_patient_ntaf_id}
            />

            {/* Partial Disbursement Modal */}
            <PartialDisbursementModal
                isOpen={partialDisbursementModalOpen}
                onClose={() => setPartialDisbursementModalOpen(false)}
                onSave={handlePartialDisbursementSave}
            />

            {/* Create Partial Disbursement Modal */}
            <CreatePartialDisbursementModal
                isOpen={createPartialDisbursementModalOpen}
                onClose={() => setCreatePartialDisbursementModalOpen(false)}
                fundRequest={fundRequest}
                onSubmit={handleCreatePartialDisbursement}
                availableFunds={availableFunds}
                rawAvailableFunds={rawAvailableFunds}
                approvedPendingTotal={approvedPendingTotal}
                unapprovedTotal={unapprovedTotal}
            />

            {/* LogEntry Modal */}
            <LogEntryModal
                isOpen={logEntryModal.isOpen}
                onClose={() => setLogEntryModal({ isOpen: false, data: null })}
                logEntryData={logEntryModal.data}
            />
        </>
    );
};

export default FundRequestDetail;
